import { useMemo } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Badge } from "../../ui/badge";
import { Skeleton } from "../../ui/skeleton";
import { TIER_COLOR } from "./macro-tier-colors";
import { formatCompactCurrency } from "../../pages/PortfolioPerformance/lib/format";
import { withVolumes } from "../../pages/PortfolioPerformance/lib/macro-volumes";
import {
  DEFAULT_SM_SORT,
  useDefaultSmScope,
  useSmIndustryPage,
} from "../../pages/PortfolioPerformance/lib/api/apd-monitoring";
import { useEpdContracts } from "../../pages/PortfolioPerformance/lib/api/epd";
import {
  useMacroExposure,
  useMacroVolumes,
} from "../../pages/PortfolioPerformance/lib/api/macro-exposure";

/** The Watchlist's tables are previews: the first few rows of the table the sub-page shows in full. */
const PREVIEW_ROWS = 5;

/**
 * One table under a Watchlist card. The whole panel is a link to the sub-page the table comes from, so it behaves
 * exactly like the card above it (same border, same hover) and lands the user on the full table.
 */
function PreviewPanel({
  to,
  title,
  description,
  footer,
  children,
}: {
  to: string;
  title: string;
  description?: string;
  footer: string;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      className="flex flex-col border border-border bg-card p-5 text-left transition-colors hover:border-primary/60 hover:bg-secondary/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
    >
      <h2 className="text-sm font-semibold uppercase tracking-wider text-foreground">{title}</h2>
      {description ? <p className="mt-1 text-xs text-muted-foreground">{description}</p> : null}
      <div className="mt-4 flex-1">{children}</div>
      <p className="mt-3 text-xs font-medium text-primary">{footer} →</p>
    </Link>
  );
}

// All three tables share these heights so their header rows and body rows line up side by side: a two-line header
// (e.g. "Months on Book") fits in the same space as a one-line one, and every body row is as tall as the EPD table's
// (the tallest, because of its status badge).
const HEAD = "pb-2 align-bottom text-left text-[10px] font-medium uppercase leading-tight tracking-wider text-muted-foreground";
const HEAD_ROW = "h-10";

/** A header over a right-aligned number column: the column's right edge stays flush with its numbers, but when the
 *  label wraps onto several lines those lines are left-aligned with each other, not ragged on the left. */
function RightHead({ children }: { children: ReactNode }) {
  return (
    <th className={HEAD + " text-right"}>
      <span className="inline-block text-left">{children}</span>
    </th>
  );
}
const BODY_ROW = "h-10 border-t border-border";
const TEXT_CELL = "truncate py-1.5 pr-2";
const NUM_CELL = "py-1.5 text-right tabular-nums";

function PreviewState({ children }: { children: ReactNode }) {
  return <p className="py-6 text-center text-xs text-muted-foreground">{children}</p>;
}

/** Special Mention Summary — by Industry (SIC): the APD Monitoring page's table, at its default view. */
export function SmIndustryPreview() {
  const scope = useDefaultSmScope();
  const industry = useSmIndustryPage(scope.riskClass, DEFAULT_SM_SORT, scope.subFilter);
  const rows = industry.data?.items.slice(0, PREVIEW_ROWS) ?? [];

  return (
    <PreviewPanel
      to="/portfolio-performance/apd-monitoring"
      title="Special Mention Summary — by Industry (SIC)"
      description="Largest net investment first"
      footer="View the full table"
    >
      {industry.isError ? (
        <PreviewState>Could not load this table.</PreviewState>
      ) : !industry.data ? (
        <Skeleton className="h-36 w-full" />
      ) : rows.length === 0 ? (
        <PreviewState>No industries to show.</PreviewState>
      ) : (
        <table className="w-full table-fixed text-xs">
          <colgroup>
            <col style={{ width: "12%" }} />
            <col style={{ width: "30%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "14%" }} />
          </colgroup>
          <thead>
            <tr className={HEAD_ROW}>
              <th className={HEAD}>SIC</th>
              <th className={HEAD}>Industry</th>
              <RightHead>NI</RightHead>
              <RightHead>% of Total</RightHead>
              <RightHead>30+ DPD</RightHead>
              <RightHead>Del %</RightHead>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.sic} className={BODY_ROW}>
                <td className={TEXT_CELL}>{r.sic}</td>
                <td className={TEXT_CELL} title={r.industry}>
                  {r.industry}
                </td>
                <td className={NUM_CELL}>{r.ni}</td>
                <td className={NUM_CELL}>{r.pctOfTotal}</td>
                <td className={NUM_CELL}>{r.amount30Plus}</td>
                <td className={NUM_CELL}>{r.delPct}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </PreviewPanel>
  );
}

// Same colouring as the EPD page's own table.
const EPD_STATUS_VARIANT: Record<string, "destructive" | "outline"> = {
  Bankruptcy: "destructive",
  Default: "destructive",
  "90+ Delinquent": "outline",
  "60+ Delinquent": "outline",
};

/** EPD Contracts: the EPD page's table without its Office and Exposure columns. */
export function EpdContractsPreview() {
  const contracts = useEpdContracts(0);
  const rows = contracts.data?.items.slice(0, PREVIEW_ROWS) ?? [];

  return (
    <PreviewPanel
      to="/portfolio-performance/epd"
      title="EPD Contracts"
      description="Contracts flagged at the latest reporting period"
      footer="View all EPD contracts"
    >
      {contracts.isError ? (
        <PreviewState>Could not load this table.</PreviewState>
      ) : !contracts.data ? (
        <Skeleton className="h-36 w-full" />
      ) : rows.length === 0 ? (
        <PreviewState>No contracts flagged.</PreviewState>
      ) : (
        <table className="w-full table-fixed text-xs">
          <colgroup>
            <col style={{ width: "27%" }} />
            <col style={{ width: "31%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "26%" }} />
          </colgroup>
          <thead>
            <tr className={HEAD_ROW}>
              <th className={HEAD}>Contract</th>
              <th className={HEAD}>Industry</th>
              <RightHead>Months on Book</RightHead>
              <th className={HEAD + " pl-3"}>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.contract} className={BODY_ROW}>
                <td className={TEXT_CELL + " font-medium text-foreground"}>{c.contract}</td>
                <td className={TEXT_CELL} title={c.industry}>
                  {c.industry}
                </td>
                <td className={NUM_CELL}>{c.monthsOnBook}</td>
                <td className="py-1.5 pl-3">
                  <Badge variant={EPD_STATUS_VARIANT[c.status] ?? "outline"} className="text-[10px]">
                    {c.status}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </PreviewPanel>
  );
}

/**
 * Borrower Industry Sensitivity, as a list: industry, its net investment, and its sensitivity band (Low, Medium or High,
 * never the score behind it), largest net investment first. The same bands and the same net investment (owned NI by
 * industry) the Macro Exposure page's borrower chart plots.
 */
export function MacroBorrowerPreview() {
  const macro = useMacroExposure();
  const volumes = useMacroVolumes();

  const rows = useMemo(() => {
    if (!macro.data) return [];
    return withVolumes(macro.data.borrower, volumes.data?.borrower)
      .slice()
      .sort((a, b) => b.volume - a.volume || b.sensitivity - a.sensitivity)
      .slice(0, PREVIEW_ROWS);
  }, [macro.data, volumes.data]);

  return (
    <PreviewPanel
      to="/portfolio-performance/macro-exposure"
      title="Borrower Industry Sensitivity"
      description="Largest net investment first"
      footer="View Macro Exposure"
    >
      {macro.isError ? (
        <PreviewState>Could not load this list.</PreviewState>
      ) : macro.isPending || volumes.isPending ? (
        // Sorted by net investment, so the list waits for it rather than flashing in the wrong order.
        <Skeleton className="h-36 w-full" />
      ) : macro.data === null ? (
        <PreviewState>Not available yet.</PreviewState>
      ) : (
        <table className="w-full table-fixed text-xs">
          <colgroup>
            <col style={{ width: "46%" }} />
            <col style={{ width: "30%" }} />
            <col style={{ width: "24%" }} />
          </colgroup>
          <thead>
            <tr className={HEAD_ROW}>
              <th className={HEAD}>Industry Name</th>
              <th className={HEAD}>Net Investment (as of {macro.data.asOf})</th>
              <th className={HEAD}>Sensitivity</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className={BODY_ROW}>
                <td className={TEXT_CELL} title={p.name}>
                  {p.name}
                </td>
                <td className="py-1.5 pr-2 text-left tabular-nums">{p.volume > 0 ? formatCompactCurrency(p.volume) : "—"}</td>
                <td className="py-1.5 text-left">
                  <span
                    className="mr-2 inline-block size-2"
                    style={{ background: TIER_COLOR[p.tier] }}
                    aria-hidden
                  />
                  <span className="font-medium" style={{ color: TIER_COLOR[p.tier] }}>
                    {p.tier}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </PreviewPanel>
  );
}
